import { memo } from 'react';
import { Cell } from '../../components/Cell.js';
import { stripImgTags } from '../../lib/format.js';
import type { ReconcileResult } from '../../api/lookup.js';

/**
 * 대조 결과. 원본 item_code_stock.php 의 '누락 상품 목록' 자리다.
 *
 * 원본은 결과를 탭으로 나눈 글자로만 보여줬다 (엑셀에 붙여넣기 위해서다).
 * 그 용도를 살려 복사용 상자를 남기고, 아래에 표를 함께 둔다.
 *
 * memo 로 감싼다 - LookupPage 는 이 컴포넌트를 붙여넣기 입력칸(keys)과
 * 조건칸(filter)의 상태와 같은 곳에 둔다. 감싸지 않으면 타이핑할 때마다
 * result 가 안 바뀌었는데도 이 표가 다시 그려진다.
 */
export const ReconcilePanel = memo(function ReconcilePanel({ result }: { result: ReconcileResult }) {
  const cols = result.columns.filter((c) => !c.hidden);
  const keyCol = cols[0]?.key ?? 'index_no';
  const nameCol = cols.find((c) => c.key === 'gname')?.key ?? cols[1]?.key ?? '';

  // 엑셀에 그대로 붙여넣도록 탭으로 나눈다 (원본과 같다).
  // 상품명(gname)엔 원본이 심어둔 <img> 아이콘 태그가 섞여 있을 수 있다 - 표는
  // Cell.tsx 가 진짜 이미지로 그려주지만, 이 상자는 순수 글자만 나가야 할
  // 엑셀용이라 태그를 걷어낸다.
  const copyText = result.missingFromInput
    .map((r) => `${String(r[keyCol] ?? '')}\t${stripImgTags(r[nameCol])}`)
    .join('\n');

  return (
    <>
      <div className="summary">
        <div className="summary__item">
          <span className="summary__label">DB 상품</span>
          <strong className="summary__value">{result.dbCount.toLocaleString()}</strong>
        </div>
        <div className="summary__item">
          <span className="summary__label">붙여넣음</span>
          <strong className="summary__value">{result.inputCount.toLocaleString()}</strong>
        </div>
        <div className="summary__item">
          <span className="summary__label">일치</span>
          <strong className="summary__value">{result.matched.toLocaleString()}</strong>
        </div>
        <div className="summary__item">
          <span className="summary__label">누락</span>
          <strong className="summary__value">
            {new Set(result.missingFromInput.map((r) => String(r[keyCol] ?? ''))).size.toLocaleString()}
          </strong>
        </div>
        <div className="summary__item">
          <span className="summary__label">DB 에 없음</span>
          <strong className="summary__value">{result.notInDb.length.toLocaleString()}</strong>
        </div>
      </div>

      {result.notInDb.length > 0 && (
        <div className="lookup-missing">
          <strong>붙여넣었는데 DB 조회 결과에 없는 코드 {result.notInDb.length}개</strong>
          {/* 원본은 이 값을 계산만 하고 보여주지 않았다. */}
          <textarea className="input" readOnly rows={3} value={result.notInDb.join('\n')} />
        </div>
      )}

      {result.missingFromInput.length === 0 ? (
        <p className="page-desc">빠진 상품이 없습니다.</p>
      ) : (
        <>
          <div className="lookup-missing">
            <strong>누락 상품 {result.missingFromInput.length}줄 (엑셀 붙여넣기용)</strong>
            <textarea className="input" readOnly rows={6} value={copyText} />
          </div>

          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  {cols.map((c) => (
                    <th key={c.key} style={{ textAlign: c.align ?? 'left' }}>
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.missingFromInput.map((row, i) => (
                  <tr key={`${String(row[keyCol] ?? '')}-${i}`}>
                    {cols.map((c) => (
                      <td key={c.key} style={{ textAlign: c.align ?? 'left' }}>
                        <Cell column={c} row={row} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
});
